import { useEffect, useState } from "react";
import useSWR from "swr";
import { Columns3, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageHeader } from "@/components/Common";
import { DevicePicker, TableList, useSelectedDevice } from "@/components/DevicePicker";
import { fetcher } from "@/lib/api";
import { fmtNum, fmtTime, MODE_LABELS, relTime } from "@/lib/format";

const PAGE = 50;

function CountCompare({ t }) {
  const src = t.source_count ?? t.est_rows ?? 0;
  if (t.received_rows == null) {
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">On server <b className="text-foreground num">{fmtNum(src)}</b>{t.source_count_exact ? "" : " (estimate)"}</span>
        <span className="text-muted-foreground" data-testid="received-count-pending">Received: counting stored rows…</span>
      </div>
    );
  }
  const diff = src - t.received_rows;
  const ok = t.source_count_exact ? diff === 0 : Math.abs(diff) <= Math.max(10, src * 0.05);
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <span className="text-muted-foreground">On server <b className="text-foreground num">{fmtNum(src)}</b>{t.source_count_exact ? "" : " (estimate)"}{t.source_count_at ? ` · counted ${relTime(t.source_count_at)}` : ""}</span>
      <span className="text-muted-foreground">Received <b className="text-foreground num">{fmtNum(t.received_rows)}</b></span>
      <span data-testid="source-vs-received-disparity-badge"
        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${ok ? "bg-[hsl(var(--ok)/0.1)] text-[hsl(var(--ok))]" : "bg-[hsl(var(--warn)/0.12)] text-[hsl(var(--warn))]"}`}>
        {ok ? "Counts match" : diff > 0 ? `${fmtNum(diff)} still to arrive` : `${fmtNum(-diff)} newer than last server count`}
      </span>
    </div>
  );
}

function SchemaSheet({ t, open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="column-schema-sheet">
        <SheetHeader><SheetTitle className="font-mono text-base">{t.db}.{t.tbl}</SheetTitle></SheetHeader>
        <p className="mt-1 text-xs text-muted-foreground">Sync method: {MODE_LABELS[t.mode] || t.mode} · key: {(t.key_columns || []).join(", ") || "none"} · time column: {t.time_column || "none"}</p>
        <Table className="mt-4">
          <TableHeader><TableRow><TableHead>Column</TableHead><TableHead>Type</TableHead><TableHead>Key</TableHead></TableRow></TableHeader>
          <TableBody>
            {(t.columns || []).map((c) => (
              <TableRow key={c.name}>
                <TableCell className="font-mono text-xs">{c.name}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{c.column_type}{c.nullable ? "" : " not null"}</TableCell>
                <TableCell className="text-xs">{c.key}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SheetContent>
    </Sheet>
  );
}

function RowsTable({ rows, columns }) {
  const show = (v) => (v == null ? <span className="text-muted-foreground">null</span> : String(v).length > 80 ? `${String(v).slice(0, 80)}…` : String(v));
  return (
    <div className="overflow-x-auto">
      <Table data-testid="data-explorer-row-preview-table">
        <TableHeader>
          <TableRow>
            {columns.map((c) => <TableHead key={c} className="whitespace-nowrap font-mono text-[11px]">{c}</TableHead>)}
            <TableHead className="whitespace-nowrap text-[11px]">Received</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {columns.map((c) => <TableCell key={c} className="whitespace-nowrap font-mono text-xs">{show(r.data[c])}</TableCell>)}
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtTime(r.received_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function TablePanel({ deviceId, t }) {
  const [page, setPage] = useState(0);
  const [field, setField] = useState("received_at");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [schema, setSchema] = useState(false);
  useEffect(() => { setPage(0); }, [t.db, t.tbl, field, start, end]);
  const q = new URLSearchParams({ device_id: deviceId, db_name: t.db, table: t.tbl, time_field: field, limit: PAGE, skip: page * PAGE });
  if (start) q.set("start", new Date(start).toISOString());
  if (end) q.set("end", new Date(end).toISOString());
  const { data } = useSWR(`/explorer/rows?${q}`, fetcher, { refreshInterval: 30000 });
  const columns = (t.columns || []).map((c) => c.name);
  const pages = data ? Math.ceil(data.total / PAGE) : 0;
  return (
    <div className="surface min-w-0 overflow-hidden">
      <div className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-mono text-base font-semibold md:text-lg" data-testid="explorer-selected-table">{t.db}.{t.tbl}</h2>
            <p className="text-xs text-muted-foreground">{columns.length} columns · {MODE_LABELS[t.mode] || t.mode}</p>
          </div>
          <Button variant="secondary" size="sm" className="rounded-full" onClick={() => setSchema(true)} data-testid="view-column-schema-button">
            <Columns3 className="mr-1.5 h-4 w-4" /> Columns
          </Button>
        </div>
        <CountCompare t={t} />
        <div className="flex flex-wrap items-end gap-2" data-testid="data-explorer-time-filter">
          <Select value={field} onValueChange={setField}>
            <SelectTrigger className="w-72 rounded-full" data-testid="explorer-time-field-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="received_at">Time received</SelectItem>
              <SelectItem value="src_ts">Time in source row{t.time_column ? ` (${t.time_column})` : ""}</SelectItem>
            </SelectContent>
          </Select>
          <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="w-52 rounded-full" data-testid="explorer-start-input" />
          <span className="pb-2 text-xs text-muted-foreground">to</span>
          <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="w-52 rounded-full" data-testid="explorer-end-input" />
          {(start || end) && <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setStart(""); setEnd(""); }} data-testid="explorer-clear-filter-button">Clear</Button>}
        </div>
      </div>
      {data && data.rows.length === 0 ? <p className="px-5 pb-6 text-sm text-muted-foreground" data-testid="explorer-no-rows">No rows in this range.</p>
        : <RowsTable rows={data?.rows || []} columns={columns} />}
      <div className="flex items-center justify-between border-t border-border/70 px-5 py-3 text-xs text-muted-foreground">
        <span className="num" data-testid="explorer-row-total">{fmtNum(data?.total)} rows{data?.partial ? " · showing the most recent batches; narrow the time range to see others" : ""}</span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)} className="rounded-full" data-testid="explorer-prev-page-button">Previous</Button>
          <span className="num">{pages ? page + 1 : 0} / {pages}</span>
          <Button variant="ghost" size="sm" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)} className="rounded-full" data-testid="explorer-next-page-button">Next</Button>
        </div>
      </div>
      <SchemaSheet t={t} open={schema} onOpenChange={setSchema} />
    </div>
  );
}

export default function Explorer() {
  const { devices, loaded, deviceId, setDeviceId } = useSelectedDevice();
  const { data: tables } = useSWR(deviceId ? `/explorer/tables?device_id=${deviceId}` : null, fetcher, { refreshInterval: 30000 });
  const [sel, setSel] = useState(null);
  const current = sel && tables?.find((t) => t.db === sel.db && t.tbl === sel.tbl);
  return (
    <div>
      <PageHeader title="Data explorer" subtitle="Browse what has arrived from each MySQL table and compare it with the row counts on the server.">
        <DevicePicker devices={devices} deviceId={deviceId} onChange={(v) => { setDeviceId(v); setSel(null); }} />
      </PageHeader>
      {loaded && (!deviceId || (tables && tables.length === 0)) ? (
        <EmptyState icon={Database} title="No tables yet" testId="explorer-empty-state"
          text="Tables appear here once the Edge Sender has scanned MySQL and sent the table list." />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
          <TableList tables={tables || []} selected={current} onSelect={setSel} renderMeta={(t) => (t.received_rows == null ? "…" : fmtNum(t.received_rows))} />
          {current ? <TablePanel deviceId={deviceId} t={current} />
            : <EmptyState icon={Database} title="Select a table" text="Choose a table on the left to preview its rows." testId="explorer-select-prompt" />}
        </div>
      )}
    </div>
  );
}
